/**
 * decor.ts — Registro de FUNDOS e DIVISORES de seção do Studio (Fase 1 da
 * biblioteca própria de efeitos e interações)
 *
 * Módulo NEUTRO (sem DOM, React, Node ou Deno). É a única fonte de verdade dos
 * fundos decorativos (degradê em malha, aurora, grade técnica, pontilhado...) e
 * dos acabamentos de borda de seção (onda, diagonal, arco, montanha,
 * zigue-zague).
 *
 * Por que existe: até aqui um fundo bonito exigia imagem ou CSS solto colado no
 * bloco. Agora ele é uma entrada de catálogo, com o MESMO contrato dos efeitos:
 *  - `version`              — versão do desenho;
 *  - `supportedRenderModes` — onde funciona de verdade;
 *  - `fallback`             — substituto quando o meio não suporta;
 *  - `performanceCost`      — custo aproximado de renderização;
 *  - `css`                  — o desenho, emitido pelo próprio registro.
 *
 * Nada aqui usa JavaScript e nada aqui aceita código do usuário: o desenho sai
 * exclusivamente das chaves registradas abaixo.
 *
 * RELEASE: v1.0.0 - 2026-09-11 - Versão inicial (biblioteca própria, Fase 1)
 */

/** Modos de saída atendidos pelo Studio. */
export type StudioDecorRenderMode = 'editor' | 'published' | 'thumbnail' | 'pdf' | 'email';

/** Custo aproximado de renderização. */
export type StudioDecorCost = 'low' | 'medium' | 'high';

/** Contrato de um fundo decorativo. */
export interface StudioBackgroundDefinition {
  key: string;
  label: string;
  description?: string;
  version: string;
  supportedRenderModes: StudioDecorRenderMode[];
  /** Fundo usado quando o meio não suporta este. `null` = sem fundo. */
  fallback: string | null;
  performanceCost: StudioDecorCost;
  /** Corpo da regra `.st-bg-<chave>`. */
  css: string;
  /** Quadros próprios (usados só pelo degradê em movimento). */
  keyframes?: string;
}

/** Contrato de um acabamento de borda (divisor de seção). */
export interface StudioDividerDefinition {
  key: string;
  label: string;
  description?: string;
  version: string;
  supportedRenderModes: StudioDecorRenderMode[];
  performanceCost: StudioDecorCost;
  /**
   * Recorte da borda. Usa `mask-image` com um desenho vetorial embutido no
   * PRÓPRIO CSS (nunca no corpo da página), portanto não passa pelo saneador
   * e não abre espaço para SVG de terceiros.
   */
  mask: string;
}

const TELA: StudioDecorRenderMode[] = ['editor', 'published'];
const TELA_E_PDF: StudioDecorRenderMode[] = [...TELA, 'pdf'];

/** Cores padrão do fundo, sobrescritas por `--st-bg-1` / `--st-bg-2`. */
const C1 = 'var(--st-bg-1, #2563eb)';
const C2 = 'var(--st-bg-2, #7c3aed)';

// =============================================================================
// Fundos decorativos
// =============================================================================

const FUNDOS: StudioBackgroundDefinition[] = [
  {
    key: 'none',
    label: 'Nenhum',
    version: '1.0.0',
    supportedRenderModes: [...TELA_E_PDF, 'email', 'thumbnail'],
    fallback: null,
    performanceCost: 'low',
    css: '',
  },
  {
    key: 'mesh',
    label: 'Degradê em malha',
    description: 'Manchas suaves de cor, no estilo dos sites de tecnologia.',
    version: '1.0.0',
    supportedRenderModes: [...TELA_E_PDF, 'thumbnail'],
    fallback: 'soft-gradient',
    performanceCost: 'medium',
    css:
      `background-color: var(--st-bg-base, #0b1020);` +
      ` background-image:` +
      ` radial-gradient(at 18% 20%, color-mix(in srgb, ${C1} 55%, transparent) 0px, transparent 55%),` +
      ` radial-gradient(at 82% 12%, color-mix(in srgb, ${C2} 50%, transparent) 0px, transparent 50%),` +
      ` radial-gradient(at 62% 88%, color-mix(in srgb, ${C1} 40%, transparent) 0px, transparent 55%);`,
  },
  {
    key: 'aurora',
    label: 'Aurora',
    description: 'Faixas de luz atravessando o fundo escuro.',
    version: '1.0.0',
    supportedRenderModes: [...TELA_E_PDF, 'thumbnail'],
    fallback: 'soft-gradient',
    performanceCost: 'medium',
    css:
      `background-color: var(--st-bg-base, #070b18);` +
      ` background-image:` +
      ` conic-gradient(from 210deg at 30% 10%, color-mix(in srgb, ${C1} 45%, transparent), transparent 40%),` +
      ` conic-gradient(from 20deg at 75% 0%, color-mix(in srgb, ${C2} 45%, transparent), transparent 45%),` +
      ` linear-gradient(180deg, transparent, rgba(0,0,0,.35));`,
  },
  {
    key: 'soft-gradient',
    label: 'Degradê suave',
    description: 'Passagem discreta de uma cor para a outra.',
    version: '1.0.0',
    supportedRenderModes: [...TELA_E_PDF, 'email', 'thumbnail'],
    fallback: null,
    performanceCost: 'low',
    css: `background-image: linear-gradient(160deg, ${C1}, ${C2});`,
  },
  {
    key: 'glow',
    label: 'Brilho radial',
    description: 'Um foco de luz no centro do topo.',
    version: '1.0.0',
    supportedRenderModes: [...TELA_E_PDF, 'thumbnail'],
    fallback: 'soft-gradient',
    performanceCost: 'low',
    css:
      `background-color: var(--st-bg-base, #0b1020);` +
      ` background-image: radial-gradient(ellipse 80% 55% at 50% 0%, color-mix(in srgb, ${C1} 55%, transparent), transparent 70%);`,
  },
  {
    key: 'grid',
    label: 'Grade técnica',
    description: 'Linhas finas em quadriculado, com esmaecimento nas bordas.',
    version: '1.0.0',
    supportedRenderModes: [...TELA_E_PDF, 'thumbnail'],
    fallback: null,
    performanceCost: 'low',
    css:
      `background-color: var(--st-bg-base, transparent);` +
      ` background-image: linear-gradient(color-mix(in srgb, ${C1} 16%, transparent) 1px, transparent 1px),` +
      ` linear-gradient(90deg, color-mix(in srgb, ${C1} 16%, transparent) 1px, transparent 1px);` +
      ` background-size: 44px 44px;` +
      ` -webkit-mask-image: radial-gradient(ellipse 75% 70% at 50% 45%, #000 45%, transparent 100%);` +
      ` mask-image: radial-gradient(ellipse 75% 70% at 50% 45%, #000 45%, transparent 100%);`,
  },
  {
    key: 'dots',
    label: 'Pontilhado',
    description: 'Malha de pontos discretos.',
    version: '1.0.0',
    supportedRenderModes: [...TELA_E_PDF, 'thumbnail'],
    fallback: null,
    performanceCost: 'low',
    css:
      `background-color: var(--st-bg-base, transparent);` +
      ` background-image: radial-gradient(color-mix(in srgb, ${C1} 26%, transparent) 1.4px, transparent 1.4px);` +
      ` background-size: 22px 22px;`,
  },
  {
    key: 'stripes',
    label: 'Listras diagonais',
    description: 'Listras finas inclinadas, muito discretas.',
    version: '1.0.0',
    supportedRenderModes: [...TELA_E_PDF, 'thumbnail'],
    fallback: null,
    performanceCost: 'low',
    css:
      `background-color: var(--st-bg-base, transparent);` +
      ` background-image: repeating-linear-gradient(135deg, color-mix(in srgb, ${C1} 10%, transparent) 0 2px, transparent 2px 12px);`,
  },
  {
    key: 'noise',
    label: 'Textura fina',
    description: 'Granulado quase imperceptível, tira o ar "chapado".',
    version: '1.0.0',
    supportedRenderModes: TELA,
    fallback: null,
    performanceCost: 'low',
    css:
      `background-color: var(--st-bg-base, transparent);` +
      ` background-image: repeating-conic-gradient(rgba(0,0,0,.035) 0% 25%, transparent 0% 50%);` +
      ` background-size: 6px 6px;`,
  },
  {
    key: 'vignette',
    label: 'Vinheta',
    description: 'Escurece as bordas e joga o olho para o centro.',
    version: '1.0.0',
    supportedRenderModes: TELA_E_PDF,
    fallback: null,
    performanceCost: 'low',
    css: 'background-image: radial-gradient(ellipse at center, transparent 45%, rgba(0,0,0,.45) 100%);',
  },
  {
    key: 'gradient-flow',
    label: 'Degradê em movimento',
    description: 'O degradê passeia bem devagar. Use uma vez por página.',
    version: '1.0.0',
    supportedRenderModes: TELA,
    fallback: 'soft-gradient',
    performanceCost: 'high',
    keyframes:
      '@keyframes st-bg-flow { 0% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } 100% { background-position: 0% 50%; } }',
    css:
      `background-image: linear-gradient(120deg, ${C1}, ${C2}, ${C1});` +
      ' background-size: 220% 220%;' +
      ' animation: st-bg-flow 18s ease-in-out infinite;',
  },
  {
    key: 'spotlight-top',
    label: 'Luz de palco',
    description: 'Cone de luz vindo do topo, ótimo para capa escura.',
    version: '1.0.0',
    supportedRenderModes: [...TELA_E_PDF, 'thumbnail'],
    fallback: 'glow',
    performanceCost: 'medium',
    css:
      `background-color: var(--st-bg-base, #05070f);` +
      ` background-image: conic-gradient(from 180deg at 50% -10%, transparent 0deg, color-mix(in srgb, ${C1} 40%, transparent) 40deg, transparent 90deg),` +
      ` radial-gradient(ellipse 60% 40% at 50% 0%, color-mix(in srgb, ${C2} 35%, transparent), transparent 70%);`,
  },
];

const MAPA_FUNDO = new Map(FUNDOS.map((f) => [f.key, f]));

/** Definição de um fundo pela chave. */
export function getBackground(key: string): StudioBackgroundDefinition | undefined {
  return MAPA_FUNDO.get(key);
}

/** Catálogo completo de fundos (cópia). */
export function listBackgrounds(): StudioBackgroundDefinition[] {
  return [...FUNDOS];
}

/** Opções prontas para o painel de propriedades. */
export const STUDIO_BACKGROUND_OPTIONS = FUNDOS.map((f) => ({ value: f.key, label: f.label }));

/** Resolve o fundo para o meio de saída, seguindo a corrente de substitutos. */
export function resolveBackground(
  key: string | undefined | null,
  mode: StudioDecorRenderMode = 'published',
): StudioBackgroundDefinition | null {
  let atual = key ? MAPA_FUNDO.get(String(key)) : undefined;
  for (let salto = 0; salto < 5; salto += 1) {
    if (!atual || atual.key === 'none') return null;
    if (atual.supportedRenderModes.includes(mode)) return atual;
    atual = atual.fallback ? MAPA_FUNDO.get(atual.fallback) : undefined;
  }
  return null;
}

// =============================================================================
// Divisores de seção (acabamento de borda)
// =============================================================================

/** Altura do acabamento. */
export const STUDIO_DIVIDER_HEIGHTS: Array<{ key: string; label: string; value: string }> = [
  { key: 'sm', label: 'Baixo', value: '36px' },
  { key: 'md', label: 'Médio', value: '64px' },
  { key: 'lg', label: 'Alto', value: '104px' },
];

export const STUDIO_DIVIDER_HEIGHT_OPTIONS = STUDIO_DIVIDER_HEIGHTS.map((h) => ({
  value: h.key,
  label: h.label,
}));

/** Desenho vetorial embutido, usado apenas dentro do CSS gerado. */
function mascara(d: string): string {
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1200 100' preserveAspectRatio='none'><path d='${d}' fill='%23000'/></svg>`;
  return `url("data:image/svg+xml,${svg.replace(/#/g, '%23').replace(/"/g, "'")}")`;
}

const DIVISORES: StudioDividerDefinition[] = [
  {
    key: 'wave',
    label: 'Onda',
    version: '1.0.0',
    supportedRenderModes: TELA,
    performanceCost: 'low',
    mask: mascara('M0,40 C300,110 900,-20 1200,50 L1200,100 L0,100 Z'),
  },
  {
    key: 'diagonal',
    label: 'Diagonal',
    version: '1.0.0',
    supportedRenderModes: TELA,
    performanceCost: 'low',
    mask: mascara('M0,100 L1200,0 L1200,100 Z'),
  },
  {
    key: 'arch',
    label: 'Arco',
    version: '1.0.0',
    supportedRenderModes: TELA,
    performanceCost: 'low',
    mask: mascara('M0,100 C300,0 900,0 1200,100 Z'),
  },
  {
    key: 'mountain',
    label: 'Montanha',
    version: '1.0.0',
    supportedRenderModes: TELA,
    performanceCost: 'low',
    mask: mascara('M0,100 L420,18 L760,74 L1200,10 L1200,100 Z'),
  },
  {
    key: 'zigzag',
    label: 'Zigue-zague',
    version: '1.0.0',
    supportedRenderModes: TELA,
    performanceCost: 'low',
    mask: mascara(
      'M0,100 L100,40 L200,100 L300,40 L400,100 L500,40 L600,100 L700,40 L800,100 L900,40 L1000,100 L1100,40 L1200,100 Z',
    ),
  },
];

const MAPA_DIVISOR = new Map(DIVISORES.map((d) => [d.key, d]));

/** Catálogo completo de divisores (cópia). */
export function listDividers(): StudioDividerDefinition[] {
  return [...DIVISORES];
}

/** Opções prontas para o painel de propriedades. */
export const STUDIO_DIVIDER_OPTIONS = [
  { value: 'none', label: 'Nenhum' },
  ...DIVISORES.map((d) => ({ value: d.key, label: d.label })),
];

// =============================================================================
// Leitura das propriedades do bloco
// =============================================================================

function texto(props: unknown, chave: string): string {
  if (!props || typeof props !== 'object') return '';
  const valor = (props as Record<string, unknown>)[chave];
  return valor == null ? '' : String(valor);
}

/** Diz se o bloco pediu algum fundo ou acabamento. */
export function temDecor(props: unknown): boolean {
  const fundo = texto(props, 'background');
  const topo = texto(props, 'dividerTop');
  const base = texto(props, 'dividerBottom');
  return (
    (!!fundo && fundo !== 'none') ||
    (!!topo && topo !== 'none') ||
    (!!base && base !== 'none')
  );
}

/**
 * Classes do fundo e dos acabamentos já resolvidas para o meio de saída.
 * String vazia quando nada se aplica (o bloco sai limpo).
 */
export function classesDoDecor(
  props: unknown,
  mode: StudioDecorRenderMode = 'published',
): string {
  if (mode === 'email') return '';
  const partes: string[] = [];

  const fundo = resolveBackground(texto(props, 'background'), mode);
  if (fundo) partes.push('st-bg', `st-bg-${fundo.key}`);

  // Acabamento de borda é coisa de tela: impressão e e-mail saem retos.
  if (mode === 'editor' || mode === 'published') {
    const topo = MAPA_DIVISOR.get(texto(props, 'dividerTop'));
    if (topo) partes.push('st-dv', `st-dv-t-${topo.key}`);
    const base = MAPA_DIVISOR.get(texto(props, 'dividerBottom'));
    if (base) partes.push('st-dv', `st-dv-b-${base.key}`);
  }

  return [...new Set(partes)].join(' ');
}

/**
 * Variáveis de cor e altura do bloco, em formato `a:b;c:d`.
 * String vazia quando o bloco não personalizou nada.
 */
export function estiloDoDecor(props: unknown): string {
  const partes: string[] = [];
  const de = texto(props, 'backgroundFrom');
  const para = texto(props, 'backgroundTo');
  const base = texto(props, 'backgroundBase');
  const corDivisor = texto(props, 'dividerColor');
  const altura = STUDIO_DIVIDER_HEIGHTS.find((h) => h.key === texto(props, 'dividerHeight'));

  // Cores só entram se forem valores simples (evita qualquer injeção em CSS).
  const corValida = (valor: string) => /^[#a-zA-Z0-9(),.\s%-]{1,40}$/.test(valor) && !/[;{}]/.test(valor);

  if (de && corValida(de)) partes.push(`--st-bg-1:${de}`);
  if (para && corValida(para)) partes.push(`--st-bg-2:${para}`);
  if (base && corValida(base)) partes.push(`--st-bg-base:${base}`);
  if (corDivisor && corValida(corDivisor)) partes.push(`--st-dv-color:${corDivisor}`);
  if (altura) partes.push(`--st-dv-h:${altura.value}`);

  return partes.join(';');
}

/** Mesmas variáveis, no formato aceito pelo React (`style={{...}}`). */
export function variaveisDoDecor(props: unknown): Record<string, string> {
  const saida: Record<string, string> = {};
  estiloDoDecor(props)
    .split(';')
    .filter(Boolean)
    .forEach((par) => {
      const corte = par.indexOf(':');
      if (corte > 0) saida[par.slice(0, corte)] = par.slice(corte + 1);
    });
  return saida;
}

// =============================================================================
// CSS emitido pelo próprio registro
// =============================================================================

export function decorStyles(): string {
  const quadros = FUNDOS.filter((f) => f.keyframes)
    .map((f) => `      ${f.keyframes}`)
    .join('\n');

  const fundos = FUNDOS.filter((f) => f.css)
    .map((f) => `      .st-bg-${f.key} { ${f.css} }`)
    .join('\n');

  const divisores = DIVISORES.map(
    (d) =>
      `      .st-dv-t-${d.key}::before { -webkit-mask-image: ${d.mask}; mask-image: ${d.mask}; transform: scaleY(-1); }\n` +
      `      .st-dv-b-${d.key}::after { -webkit-mask-image: ${d.mask}; mask-image: ${d.mask}; }`,
  ).join('\n');

  return `      /* Fundos decorativos e acabamentos de borda (biblioteca própria) */
      .st-bg { position: relative; background-repeat: no-repeat; background-position: center; }
${quadros}
${fundos}
      .st-dv { position: relative; }
      .st-dv::before, .st-dv::after {
        content: ""; position: absolute; left: 0; right: 0; pointer-events: none;
        height: var(--st-dv-h, 64px);
        background: var(--st-dv-color, #ffffff);
        -webkit-mask-size: 100% 100%; mask-size: 100% 100%;
        -webkit-mask-repeat: no-repeat; mask-repeat: no-repeat;
      }
      .st-dv::before { top: 0; }
      .st-dv::after { bottom: 0; }
${divisores}
      @media print {
        .st-dv::before, .st-dv::after { display: none; }
        .st-bg-gradient-flow { animation: none; }
      }
      @media (prefers-reduced-motion: reduce) {
        .st-bg-gradient-flow { animation: none; }
      }`;
}
